import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const TRANSACTIONS_PER_PAGE = 20;
const defaultValue = { count: 0, hasMore: false, refresh: () => {} };
const PendingTransactionsBadgeContext = createContext(defaultValue);

/**
 * Hook for components to read the pending transactions badge state.
 * @returns {{count: number, hasMore: boolean, refresh: Function}}
 */
export function usePendingTransactionsBadge() {
    return useContext(PendingTransactionsBadgeContext);
}

/**
 * Fetches the first page of pending transactions and updates state.
 * Silent failure — sidebar badge is non-critical, never blocks the UI.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchCount({ signal, setCount, setHasMore }) {
    try {
        const { data } = await axios.get("/admin/transactions/pending/per_page/1");
        if (signal.aborted) return;
        const items = Array.isArray(data) ? data : [];
        setCount(items.length);
        setHasMore(items.length >= TRANSACTIONS_PER_PAGE);
    } catch {
        if (!signal.aborted) {
            setCount(0);
            setHasMore(false);
        }
    }
}

/**
 * Context provider that fetches the pending transactions count on mount and
 * exposes a refresh function so the transactions page can re-sync after approve.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components consuming the context
 * @returns {React.ReactElement}
 */
export function PendingTransactionsBadgeProvider({ children }) {
    const [count, setCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        fetchCount({ signal: controller.signal, setCount, setHasMore });
        return () => controller.abort();
    }, [tick]);

    const refresh = useCallback(() => setTick((t) => t + 1), []);

    return (
        <PendingTransactionsBadgeContext.Provider value={{ count, hasMore, refresh }}>
            {children}
        </PendingTransactionsBadgeContext.Provider>
    );
}
